import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { usePageTitle } from '../../../lib/hooks';
import { defineListPage } from '@repo/list';
import { DataTable } from '@repo/web-list';
import { FormSelect } from '../../../components/ui/FormSelect';
import { useQuery } from '@tanstack/react-query';
import { fetcher } from '../../../lib/api-fetcher';
import { pollPdfJobStatus, unwrapData, type QueuedPdfJob } from '../../../features/reports/client';

export const Route = createFileRoute('/_auth/compliance/subscriber-register')({
  component: SubscriberRegisterPage,
});

type Subscriber = {
  id: string;
  ticketNo: number;
  name: string;
  joinedDate: string;
  status: 'active' | 'defaulted' | 'completed';
};

const subscriberConfig = defineListPage<Subscriber>({
  variant: 'data-table',
  resource: 'subscribers',
  columns: [
    { id: 'ticketNo', header: 'Ticket No', accessor: 'ticketNo' as const, renderType: 'numeric', numeric: true },
    { id: 'name', header: 'Name', accessor: 'name' as const, renderType: 'text' },
    { id: 'joinedDate', header: 'Joined Date', accessor: 'joinedDate' as const, renderType: 'date' },
    { id: 'status', header: 'Status', accessor: 'status' as const, renderType: 'status', renderMeta: { colorMap: { active: 'success', defaulted: 'danger', completed: 'info' } } },
  ],
  defaultPageSize: 10,
});

function SubscriberRegisterPage() {
  usePageTitle('Subscriber Register');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [page, setPage] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: schemesResponse } = useQuery({
    queryKey: ['schemes', 'list'],
    queryFn: () => fetcher.get<{ data: any[]; meta: any }>('/staff/v1/chit-groups'),
  });

  const schemeOptions = [
    { value: '', label: '-- Choose a Chit Scheme --' },
    ...(schemesResponse?.data?.map(s => ({ value: s.id, label: `${s.displayName ?? s.name ?? s.schemeNumber} (${s.schemeNumber})` })) ?? []),
  ];

  const { data: subscribersResponse, isLoading, isError, refetch } = useQuery({
    queryKey: ['group-subscribers', selectedGroup, page],
    queryFn: () => fetcher.get<{ data: any[]; meta: any }>(`/staff/v1/chit-groups/${selectedGroup}/subscribers?page=${page}&limit=10`),
    enabled: !!selectedGroup,
  });

  const subscribers = (subscribersResponse?.data ?? []).map((s: any) => ({
    id: s.id,
    ticketNo: s.ticketNumbers?.[0] ?? 0,
    name: s.customerName,
    joinedDate: s.enrolledAt ? new Date(s.enrolledAt).toISOString().split('T')[0] : '',
    status: s.status as 'active' | 'defaulted' | 'completed',
  }));

  const handlePrint = async () => {
    if (!selectedGroup || isGenerating) return;
    setIsGenerating(true);
    try {
      // P0-003: the endpoint is async (202 + jobId) — enqueue, poll the shared
      // pdf status route until ready, then open the finished file.
      const queuedRes = await fetcher.get<QueuedPdfJob | { data: QueuedPdfJob }>(`/staff/v1/compliance/schemes/${selectedGroup}/subscriber-register`);
      const queued = unwrapData<QueuedPdfJob>(queuedRes);
      const status = await pollPdfJobStatus(queued.jobId);
      if (status.status !== 'COMPLETED' || !status.result?.storageRef) {
        throw new Error(status.error ?? 'Subscriber register PDF generation failed');
      }
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      window.open(`${apiBaseUrl}/api/common/storage/files/${status.result.storageRef}`, '_blank');
    } catch (err) {
      console.error('Failed to generate Section 12 register PDF:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const listState = {
    data: subscribers,
    meta: { total: subscribersResponse?.meta?.total ?? subscribers.length, page, limit: 10 },
    isLoading,
    isError,
    error: null as Error | null,
    refetch,
    page,
    setPage,
    search: '',
    setSearch: () => {},
    sort: null,
    setSort: () => {},
    filters: {},
    setFilter: () => {},
    resetFilters: () => {},
    resetAll: () => {},
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscriber Register (Section 12)"
        subtitle="View and manage compliance subscriber records under each chit scheme."
        action={
          <Button
            variant="primary"
            disabled={!selectedGroup || isGenerating}
            onClick={handlePrint}
          >
            {isGenerating ? 'Generating…' : 'Print Register'}
          </Button>
        }
      />
      <div className="mb-8 max-w-sm">
        <FormSelect
          label="Select Scheme"
          options={schemeOptions}
          value={selectedGroup}
          onChange={(e) => {
            setSelectedGroup(e.target.value);
            setPage(1);
          }}
        />
      </div>
      {selectedGroup ? (
        <PageSection title="Register">
          <DataTable config={subscriberConfig} list={listState} />
        </PageSection>
      ) : (
        <div className="text-center text-text-secondary py-12 border border-dashed border-border rounded-lg bg-surface-1">
          Please select a scheme to view its subscriber register.
        </div>
      )}
    </div>
  );
}
